import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import {
  ArrowLeft,
  MessageSquare,
  FileText,
  Clock,
  Calendar,
  Loader2,
  ChevronRight,
  History,
  Target
} from 'lucide-react';

interface Session {
  id: number;
  session_id: string;
  patient_id: string;
  patient_name: string;
  model_name: string;
  is_intervention: boolean;
  message_count: number;
  created_at: string;
  updated_at: string;
}

const HistoryPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchSessions();
  }, []);

  const fetchSessions = async () => {
    try {
      const response = await apiClient.get('/api/sessions/');
      setSessions(response.data.sessions);
    } catch (err: any) {
      console.error('Failed to fetch session history:', err);
      setError('Failed to load your history. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToDashboard = async () => {
    try {
      const response = await apiClient.get('/api/auth/next-task');
      const { redirect, patient_id } = response.data;

      if (redirect === 'chart') {
        navigate(`/patient-chart/${patient_id}`);
      } else {
        navigate('/patient-chart/patient_0');
      }
    } catch (err) {
      navigate('/patient-chart/patient_0');
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary mb-2" />
        <p className="text-muted-foreground">Loading your history...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 pb-12">
      {/* Header */}
      <div className="bg-primary text-primary-foreground shadow-md">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleBackToDashboard}
              className="text-primary-foreground hover:bg-primary-foreground/10"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <h1 className="text-xl font-bold flex items-center gap-2">
              <History className="h-5 w-5" />
              Interview History
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium mr-2 hidden sm:inline">{user?.username}</span>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 max-w-5xl">
        <Card className="mb-8 border-none shadow-lg bg-white/80 backdrop-blur dark:bg-gray-900/80">
          <CardHeader>
            <CardTitle className="text-2xl font-bold">Your Completed Cases</CardTitle>
            <CardDescription>
              Review your past performance and conversation transcripts across all patient encounters.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {error ? (
              <div className="bg-red-50 text-red-700 p-4 rounded-md border border-red-100 mb-4">
                {error}
              </div>
            ) : sessions.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 rounded-lg border border-dashed">
                <History className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900">No sessions yet</h3>
                <p className="text-gray-500 mt-1">Complete your first case to see it here.</p>
                <Button onClick={handleBackToDashboard} className="mt-6">
                  Begin Study
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {sessions.map((session) => (
                  <Card key={session.session_id} className="overflow-hidden hover:shadow-md transition-shadow border-gray-200">
                    <div className="flex flex-col md:flex-row md:items-center justify-between p-5 gap-4">
                      <div className="space-y-1 flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">
                            {session.patient_name}
                          </h3>
                          <Badge variant={session.is_intervention ? "secondary" : "default"} className={session.is_intervention ? "bg-purple-100 text-purple-800 border-purple-200" : "bg-blue-100 text-blue-800 border-blue-200"}>
                            {session.is_intervention ? "Intervention" : "Confirmation"}
                          </Badge>
                        </div>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-500">
                          <div className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {new Date(session.created_at).toLocaleDateString()}
                          </div>
                          <div className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {new Date(session.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </div>
                          <div className="flex items-center gap-1">
                            <MessageSquare className="h-3 w-3" />
                            {session.message_count} messages
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2 self-end md:self-center">
                        {!session.is_intervention && (
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => navigate(`/evaluation/${session.session_id}`)}
                            className="gap-1 border-orange-200 text-orange-700 hover:bg-orange-50"
                          >
                            <Target className="h-4 w-4" />
                            Evaluation
                          </Button>
                        )}
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => navigate(`/session/${session.session_id}`)}
                          className="gap-1"
                        >
                          <FileText className="h-4 w-4" />
                          Transcript
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          onClick={() => navigate(`/session/${session.session_id}`)}
                          className="text-gray-400"
                        >
                          <ChevronRight className="h-5 w-5" />
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default HistoryPage;
